import { useState, useRef } from "react";
import Modal from "aldoo-ra/Modal";
import Button from "aldoo-ra/Button";
import Input from "aldoo-ra/Input";
import Typography from "aldoo-ra/Typography";
import ToggleSwitch from "aldoo-ra/ToggleSwitch";
import {
  showSuccessToast,
  showErrorToast,
  TOAST_POSITIONS,
} from "aldoo-ra/Toast";
import {
  ClockIcon,
  SwatchIcon,
  UserGroupIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  ChatBubbleLeftIcon,
  GlobeAltIcon,
  LockClosedIcon,
  UsersIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import ThemeSelect from "../ShareThemes/ThemeSelect";
import { ACCOUNT_DEFAULT_ID } from "../ShareThemes/constants";
import UserAccessWidget from "../UserAccessWidget";
import TextBundle from "aldoo-ra/CMS/text-bundle";
import { createLink, updateLink } from "./controller";

// Create / edit dialog. When `link` is null we POST create; otherwise we PATCH
// the existing receive-link. The shape mirrors the existing share-create UI
// so the same primitives (retention chips, theme dropdown, password input)
// are reusable.
const LINK_TTL_OPTIONS = [7, 14, 30, 90]; // days the link itself stays open

export default function ReceiveLinkDialog({ link, onClose, onSaved }) {
  const editing = !!link;

  const [name, setName] = useState(link?.name || "");
  const [senderMessage, setSenderMessage] = useState(
    link?.sender_message || "",
  );
  const [accessType, setAccessType] = useState(
    link?.presets?.access_type || "public",
  );
  const [themeId, setThemeId] = useState(
    link?.presets?.theme_id || ACCOUNT_DEFAULT_ID,
  );
  const [password, setPassword] = useState(""); // never prefilled
  const [hasPassword, setHasPassword] = useState(!!link?.presets?.password_set);
  const [linkTtl, setLinkTtl] = useState(7); // for create only — default 7d window
  const [notifyOwner, setNotifyOwner] = useState(link?.notify_owner !== false);
  const [accessEmails, setAccessEmails] = useState(link?.presets?.access || []);
  const [showAccessDrawer, setShowAccessDrawer] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(
    !!link?.presets?.password_set,
  );
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState("title");
  const [saving, setSaving] = useState(false);
  const [showTabHint, setShowTabHint] = useState(true);
  const tabBarRef = useRef(null);

  const checkTabScroll = () => {
    const el = tabBarRef.current;
    if (!el) return;
    setShowTabHint(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  const texts = TextBundle({ id: "transfer_receive_links" });

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const linkExpiresAt = editing
        ? undefined
        : new Date(Date.now() + linkTtl * 24 * 60 * 60 * 1000).toISOString();

      const presets = {
        // Received-file retention is derived server-side from the plan
        // feature matrix (shortest plan-allowed duration). Recipients can
        // extend after receipt via the share-extend flow.
        access_type: accessType,
        access: accessType === "authenticated" ? accessEmails : [],
        theme_id: themeId && /^[0-9a-fA-F]{24}$/.test(themeId) ? themeId : null,
        theme_mode:
          themeId === ACCOUNT_DEFAULT_ID
            ? "account"
            : themeId === "__default__"
              ? "none"
              : "specific",
        // Senders pick their own download limits per-transfer; the
        // link itself doesn't enforce one. Transfer volume is gated
        // by the owner's plan quota at receive time.
        download_limit: null,
      };
      if (passwordEnabled && password) presets.password = password;
      if (editing && (!passwordEnabled || (!hasPassword && !password)))
        presets.password = "";

      const payload = {
        name,
        sender_message: senderMessage,
        presets,
        // Quota is plan-enforced per-upload against the owner's
        // account, not capped per-link.
        max_transfers: null,
        notify_owner: notifyOwner,
      };
      if (!editing) payload.link_expires_at = linkExpiresAt;

      const res = editing
        ? await updateLink(link._id, payload)
        : await createLink(payload);

      if (res?.error) {
        showErrorToast(texts?.saveFailed, res.error, {
          position: TOAST_POSITIONS.TOP_CENTER,
          pauseOnHover: false,
          enableDismissalTracking: false,
        });
        return;
      }
      if (res?.result) {
        showSuccessToast(
          editing ? texts?.linkUpdated : texts?.linkCreated,
          "",
          {
            position: TOAST_POSITIONS.TOP_CENTER,
            pauseOnHover: false,
            enableDismissalTracking: false,
            duration: 1800,
          },
        );
        onSaved?.(res.link);
        onClose?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-2 w-full">
      <Button variant="secondary" onClick={onClose} disabled={saving}>
        {texts?.cancel}
      </Button>
      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? texts?.saving : editing ? texts?.save : texts?.createLink}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={editing ? texts?.editLink : texts?.newFilesRequest}
      variant="transfer_medium"
      closeOnClickOutside={false}
      closeOnEscape={!saving}
      footerContent={footerContent}
    >
      <div className="py-5 md:px-5 flex flex-col gap-4">
        {/* Tabs — mirrors the create-share UI (title / message / duration / theme / access / password). */}
        <div>
          <div className="relative">
            <div
              ref={tabBarRef}
              onScroll={checkTabScroll}
              className="flex sm:justify-center gap-1 border-b border-bordered mb-4 -mx-2 overflow-x-auto no-scrollbar"
            >
              {[
                { id: "title", label: texts?.title, icon: PencilSquareIcon },
                { id: "access", label: texts?.userAccess, icon: UserGroupIcon },
                {
                  id: "message",
                  label: texts?.message,
                  icon: ChatBubbleLeftIcon,
                },
                {
                  id: "duration",
                  label: texts?.duration,
                  icon: ClockIcon,
                  hide: editing,
                },
                { id: "theme", label: texts?.theme, icon: SwatchIcon },
                { id: "password", label: texts?.password, icon: KeyIcon },
              ]
                .filter((t) => !t.hide)
                .map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                      tab === id
                        ? "border-primary text-primary"
                        : "border-transparent text-text_secondary hover:text-text"
                    }`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2} />
                    {label}
                  </button>
                ))}
            </div>
            {showTabHint && (
              <div className="sm:hidden pointer-events-none absolute -right-2 top-0 h-[38px] w-12 z-10 flex items-center justify-end">
                <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-paper to-transparent" />
                <ChevronRightIcon className="relative w-4 h-4 text-text_secondary mr-1" />
              </div>
            )}
          </div>

          {/* Title tab — what senders see at the top of the upload page. */}
          {tab === "title" && (
            <div>
              <Typography variant="txt2_dim" className="block mb-3 text-center">
                {texts?.titleDesc}
              </Typography>
              <Input
                variant="transfer_compact"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onEnter={handleSave}
                placeholder={texts?.linkNamePh}
                className="w-full"
                autoFocus
              />
            </div>
          )}

          {/* Message tab — instructions / context shown to senders. */}
          {tab === "message" && (
            <div>
              <Typography variant="txt2_dim" className="block mb-3 text-center">
                {texts?.messageDesc}
              </Typography>
              <textarea
                value={senderMessage}
                onChange={(e) => setSenderMessage(e.target.value)}
                rows={5}
                placeholder={texts?.senderMessagePh}
                className="w-full rounded-xl border border-bordered bg-paper px-3 py-2.5 text-sm text-text placeholder-text_secondary focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
              />
            </div>
          )}

          {/* Duration tab — link TTL chips. */}
          {tab === "duration" && !editing && (
            <div>
              <Typography variant="txt2_dim" className="block mb-3 text-center">
                {texts?.linkValidFor}
              </Typography>
              <div className="flex gap-2 flex-wrap justify-center">
                {LINK_TTL_OPTIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setLinkTtl(d)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      linkTtl === d
                        ? "border-primary text-white bg-primary"
                        : "border-bordered text-text hover:border-primary"
                    }`}
                  >
                    {d} {texts?.days}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Theme tab. */}
          {tab === "theme" && (
            <div>
              <Typography variant="txt2_dim" className="block mb-3 text-center">
                {texts?.themeDesc}
              </Typography>
              <ThemeSelect value={themeId} onChange={setThemeId} />
            </div>
          )}

          {/* Access tab — 1:1 with the create-share access tab:
two big buttons (Public / Authenticated) and a
"Manage user access" CTA once authenticated. */}
          {tab === "access" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "public",
                    label: texts?.accessPublic,
                    desc: texts?.accessPublicDesc,
                    icon: GlobeAltIcon,
                  },
                  {
                    value: "authenticated",
                    label: texts?.accessAuthenticated,
                    desc: texts?.accessAuthenticatedDesc,
                    icon: LockClosedIcon,
                  },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setAccessType(opt.value);
                        if (opt.value === "authenticated")
                          setShowAccessDrawer(true);
                      }}
                      className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${
                        accessType === opt.value
                          ? "border-primary bg-primary/10"
                          : "border-bordered hover:border-text_secondary"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <Icon className="h-4 w-4 text-text_secondary" />
                        <span className="text-sm font-medium text-text">
                          {opt.label}
                        </span>
                      </div>
                      <div className="text-xs text-text_secondary">
                        {opt.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
              {accessType === "authenticated" && (
                <button
                  type="button"
                  onClick={() => setShowAccessDrawer((v) => !v)}
                  className="mt-3 w-full flex items-center justify-between gap-2 rounded-full border border-primary px-4 py-2.5 text-sm transition-colors"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <UsersIcon className="hidden sm:block h-4 w-4" />
                    <span>{texts?.manageUserAccess}</span>
                  </div>
                  <span className="text-xs bg-primary rounded-full px-2 py-0.5 text-white font-medium">
                    {accessEmails.length === 0
                      ? texts?.addUsers
                      : `${accessEmails.length} ${accessEmails.length === 1 ? texts?.user : texts?.users}`}
                  </span>
                </button>
              )}
              {accessType === "authenticated" && showAccessDrawer && (
                <div className="mt-3 bg-canvas border border-bordered rounded-2xl p-3">
                  <UserAccessWidget
                    emails={accessEmails}
                    onAdd={(email) => {
                      setAccessEmails((prev) =>
                        prev.includes(email) ? prev : [...prev, email],
                      );
                    }}
                    onRemove={(email) => {
                      setAccessEmails((prev) =>
                        prev.filter((e) => e !== email),
                      );
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Password tab — toggle-row + reveal input, 1:1 with the
create-share password tab. */}
          {tab === "password" && (
            <div>
              <div className="w-full flex items-center justify-between px-1 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <KeyIcon className="h-4 w-4 text-text_secondary" />
                  <span className="font-medium text-text">
                    {texts?.passwordProtection}
                  </span>
                </div>
                <ToggleSwitch
                  isOn={passwordEnabled}
                  onChange={(val) => {
                    setPasswordEnabled(val);
                    if (!val) {
                      setPassword("");
                      setHasPassword(false);
                    }
                  }}
                />
              </div>
              {passwordEnabled && (
                <div className="relative mt-2">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      editing && hasPassword
                        ? texts?.leaveBlankKeep
                        : texts?.enterPassword
                    }
                    className="w-full rounded-lg bg-transparent px-3 py-2 pr-10 text-sm text-text placeholder-gray-400 focus:outline-none border border-bordered"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notify — always visible at the bottom. */}
        <div className="flex items-center justify-between border-t border-bordered pt-3 mt-1">
          <div className="flex flex-col">
            <Typography variant="txt1_accent">{texts?.notifyOwner}</Typography>
            <Typography variant="txt2_dim">{texts?.notifyOwnerHelp}</Typography>
          </div>
          <ToggleSwitch isOn={notifyOwner} onChange={setNotifyOwner} />
        </div>
      </div>
    </Modal>
  );
}
